import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Upload } from "lucide-react";

const LABELS = {
  hero_bg: "Homepage hero background",
  tracker: "Site tracker / analytics code (loads on EVERY page)",
  contact_form: "Contact page form (forms.app embed HTML or link)",
  casting_form: "Casting page form (optional forms.app embed HTML or link)",
  application: "Application page form (forms.app embed HTML or link)",
};

export default function AdminSite() {
  const [embeds, setEmbeds] = useState([]);
  const [heroType, setHeroType] = useState("image");
  const [saving, setSaving] = useState(null);
  const [heroBusy, setHeroBusy] = useState(false);
  const heroRef = useRef();

  const load = () => api.get("/admin/embeds").then((r) => {
    setEmbeds(r.data);
    const h = r.data.find((e) => e.key === "hero_bg");
    if (h && h.title) setHeroType(h.title);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setField = (key, field, val) => setEmbeds((prev) => prev.map((e) => e.key === key ? { ...e, [field]: val } : e));

  const save = async (key, extra = {}) => {
    const e = embeds.find((x) => x.key === key) || { key, html: "", title: "" };
    setSaving(key);
    try { await api.put(`/admin/embeds/${key}`, { html: e.html || "", title: extra.title ?? e.title ?? "" }); toast.success("Saved"); }
    catch (err) { toast.error("Save failed"); } finally { setSaving(null); }
  };

  const uploadHero = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setHeroBusy(true);
    const t = file.type.startsWith("video") ? "video" : "image";
    const fd = new FormData(); fd.append("file", file); fd.append("kind", "hero");
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeroType(t); setField("hero_bg", "html", data.url); setField("hero_bg", "title", t);
      await api.put("/admin/embeds/hero_bg", { html: data.url, title: t });
      toast.success("Hero background uploaded & saved");
    } catch (err) { toast.error("Upload failed (large videos may exceed 100MB)"); }
    finally { setHeroBusy(false); e.target.value = ""; }
  };

  const hero = embeds.find((e) => e.key === "hero_bg") || { key: "hero_bg", html: "", title: "image" };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold">Site settings</h1>
      <p className="mt-1 text-sm text-zinc-500">Set the homepage hero background and paste forms.app embed codes for your forms.</p>

      <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-5">
        <h2 className="font-heading font-bold">{LABELS.hero_bg}</h2>
        <p className="mt-1 text-sm text-zinc-500">Upload an image or video, or paste a URL. Leave blank to use the default studio image.</p>
        {hero.html && (
          <div className="mt-3 h-40 w-full overflow-hidden rounded-lg bg-zinc-900">
            {heroType === "video"
              ? <video src={mediaUrl(hero.html)} className="h-full w-full object-cover" muted autoPlay loop playsInline />
              : <img src={mediaUrl(hero.html)} alt="" className="h-full w-full object-cover" />}
          </div>
        )}
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div><Label className="label-eyebrow text-zinc-500">Type</Label>
            <Select value={heroType} onValueChange={(v) => { setHeroType(v); setField("hero_bg", "title", v); }}>
              <SelectTrigger className="h-10 w-32 rounded-lg mt-1" data-testid="hero-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="image">Image</SelectItem><SelectItem value="video">Video</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]"><Label className="label-eyebrow text-zinc-500">Media URL</Label>
            <Input value={hero.html || ""} onChange={(e) => setField("hero_bg", "html", e.target.value)} placeholder="https://…" data-testid="hero-url" className="h-10 rounded-lg mt-1" /></div>
          <input ref={heroRef} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" hidden onChange={uploadHero} data-testid="hero-upload-input" />
          <button onClick={() => heroRef.current?.click()} disabled={heroBusy} data-testid="hero-upload" className="inline-flex items-center gap-2 rounded-full border border-zinc-300 px-5 py-2.5 text-sm font-bold hover:border-brand-violet disabled:opacity-60">
            {heroBusy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
          </button>
          <button onClick={() => save("hero_bg", { title: heroType })} disabled={saving === "hero_bg"} data-testid="save-hero" className="inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover">
            {saving === "hero_bg" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
        </div>
      </div>

      {["tracker", "contact_form", "application", "casting_form"].map((key) => {
        const e = embeds.find((x) => x.key === key) || { key, html: "", title: "" };
        return (
          <div key={key} className="mt-4 rounded-xl border border-zinc-200 bg-white p-5">
            <h2 className="font-heading font-bold">{LABELS[key]}</h2>
            <Textarea value={e.html || ""} onChange={(ev) => setField(key, "html", ev.target.value)} rows={5}
              placeholder="Paste your forms.app link (e.g. https://xxxx.forms.app/contact-form) or full embed code" data-testid={`embed-${key}`}
              className="mt-3 rounded-lg font-mono text-xs" />
            <button onClick={() => save(key)} disabled={saving === key} data-testid={`save-embed-${key}`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-violet px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-violetHover">
              {saving === key ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
            </button>
          </div>
        );
      })}
    </div>
  );
}
